/* Copyright (C) Okahu Inc 2023-2024. All rights reserved. */

'use client';

import { Dispatch, SetStateAction, useState } from 'react';
import { useLanguage } from '@/providers/LanguageProvider';
import { CheckedState } from '@radix-ui/react-checkbox';
import { ChevronRight } from 'lucide-react';

import { cn } from '@/lib/utils';

import BasicLoader from '../spinner/basic-loader';
import { Checkbox } from '../ui/checkbox';
import { Label } from '../ui/label';

interface CriteriaDataItem {
  id: string;
  name: string;
  children?: CriteriaDataItem[];
}

interface TreeDropdownCheckboxProps {
  data: CriteriaDataItem[] | undefined;
  selectedItem?: CriteriaDataItem | null;
  selectedIds: string[];
  setSelectedIds: Dispatch<SetStateAction<string[]>>;
  loading?: boolean;
}

const TreeDropdownCheckbox: React.FC<TreeDropdownCheckboxProps> = ({
  data,
  selectedItem,
  selectedIds,
  setSelectedIds,
  loading,
}) => {
  const { messages } = useLanguage();
  const langData = messages?.General;
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());

  const toggleExpanded = (itemId: string) => {
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(itemId)) {
      newExpanded.delete(itemId);
    } else {
      newExpanded.add(itemId);
    }
    setExpandedItems(newExpanded);
  };

  // Helper to get all descendant IDs recursively
  const getAllDescendantIds = (item: CriteriaDataItem): string[] => {
    if (!item.children || item.children.length === 0) return [];
    return item.children.reduce<string[]>(
      (acc, child) => acc.concat(child.id, getAllDescendantIds(child)),
      []
    );
  };

  // Helper to determine checkbox state: true, false, or 'indeterminate'
  const getCheckboxState = (item: CriteriaDataItem): CheckedState => {
    if (!item.children || item.children.length === 0) {
      return selectedIds.includes(item.id);
    }
    const allChildIds = getAllDescendantIds(item);
    const selectedChildren = allChildIds.filter((id) =>
      selectedIds.includes(id)
    );
    if (selectedChildren && selectedChildren.length === 0) {
      return selectedIds.includes(item.id);
    }
    if (selectedChildren && selectedChildren.length === allChildIds.length) {
      // All children selected, parent may or may not be selected
      return true;
    }
    // Some children selected
    return 'indeterminate';
  };

  const handleChecked = (checked: CheckedState, item: CriteriaDataItem) => {
    let newSelectedIds: string[];
    const allChildIds = getAllDescendantIds(item);

    if (checked) {
      // Add parent and all children
      newSelectedIds = Array.from(
        new Set([...selectedIds, item.id, ...allChildIds])
      );
    } else {
      // Remove parent and all children
      newSelectedIds = selectedIds.filter(
        (id) => id !== item.id && !allChildIds.includes(id)
      );

      // If this is a child, check if all siblings are now deselected, then deselect parent
      if (item.id.startsWith('parent_')) {
        // Find parent in data
        const parent = data?.find((d) =>
          d.children?.some((child) => child.id === item.id)
        );
        if (parent) {
          const siblings = parent.children ?? [];
          const siblingsSelected = siblings.some((child) =>
            newSelectedIds.includes(child.id)
          );
          if (!siblingsSelected) {
            newSelectedIds = newSelectedIds.filter((id) => id !== parent.id);
          }
        }
      }
    }
    setSelectedIds(newSelectedIds);
  };

  const renderTreeItem = (
    item: CriteriaDataItem,
    level: number = 0,
    isLast: boolean = false,
    parentLines: boolean[] = []
  ) => {
    const hasChildren = item.children && item.children.length > 0;
    const isExpanded = expandedItems.has(item.id);
    const isSelected = selectedItem?.id === item.id;
    return (
      <div key={item.id}>
        <div
          className={cn(
            'relative flex cursor-pointer items-center py-2 text-sm',
            isSelected && 'bg-[#EDEBE9]'
          )}
        >
          {/* Dotted lines */}
          {level > 0 && (
            <>
              {/* L-shaped connector for current item */}
              {Array.from({ length: level }).map(
                (_, idx) =>
                  (idx === level - 1 || parentLines[idx]) && (
                    <div
                      key={idx}
                      className="absolute border-l border-dotted border-line-secondary"
                      style={{
                        left: `${16 + idx * 24}px`,
                        top: 0,
                        height: '50%',
                        width: '1px',
                      }}
                    />
                  )
              )}
              <div
                className="absolute border-t border-dotted border-line-secondary"
                style={{
                  left: `${16 + (level - 1) * 24}px`,
                  top: '50%',
                  width: hasChildren ? '16px' : '40px',
                  height: '1px',
                }}
              />

              {/* Continue vertical line down if not last item */}
              {Array.from({ length: isLast ? level - 1 : level }).map(
                (_, idx) =>
                  parentLines[idx] && (
                    <div
                      key={idx}
                      className="absolute border-l border-dotted border-line-secondary"
                      style={{
                        left: `${16 + idx * 24}px`,
                        top: '50%',
                        bottom: 0,
                        width: '1px',
                      }}
                    />
                  )
              )}
            </>
          )}

          <div
            className="flex flex-1 items-center gap-2"
            style={{
              paddingLeft: `${level > 0 ? level * 24 + 8 : 8}px`,
            }}
          >
            <ChevronRight
              className={cn(
                'mr-1 h-4 w-4 shrink-0 text-[#8C9EA1] transition-transform duration-200',
                isExpanded && 'rotate-90',
                !hasChildren && 'opacity-0'
              )}
              onClick={(e) => {
                e.stopPropagation();
                toggleExpanded(item.id);
              }}
            />

            <div key={item.id} className="flex items-center gap-1.5">
              <Checkbox
                id={item.id}
                className="border-form-control-border-rest"
                checked={getCheckboxState(item)}
                onCheckedChange={(checked: CheckedState) =>
                  handleChecked(checked, item)
                }
              />
              <Label htmlFor={item.id}>{item.name}</Label>
            </div>
          </div>
        </div>

        {/* Children */}
        {hasChildren && isExpanded && (
          <div>
            {item.children!.map((child, index, array) => {
              const isChildLast = index === array.length - 1;
              const newParentLines = [...parentLines, !isChildLast];
              return renderTreeItem(
                child,
                level + 1,
                isChildLast,
                newParentLines
              );
            })}
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex max-h-[300px] w-full items-center justify-center border border-white p-0 transition-transform duration-200">
        <BasicLoader className="m-2 size-6" />
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="w-full border border-form-control-border-rest p-3 text-center text-sm font-medium text-[#8C9EA1] transition-transform duration-200">
        {langData?.no_data_found}
      </div>
    );
  }

  return (
    <div className="w-full border border-form-control-border-rest p-0 transition-transform duration-200">
      <div className="max-h-[300px] overflow-y-auto rounded-none text-white">
        {data?.map((item, index, array) =>
          renderTreeItem(item, 0, index === array.length - 1, [])
        )}
      </div>
    </div>
  );
};

export { TreeDropdownCheckbox, type CriteriaDataItem };
